import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { smlNavigationChromeMessages as messages } from '../smlNavigationMessages';
import styles from './MobileSheet.module.scss';

interface MobileSheetProps {
  id: string;
  /** Text, or a node that reads its own text (the workspace name of the account sheet). */
  title: React.ReactNode;
  description?: string;
  /** A short line at the right of the title, such as the artist count of the switcher sheet. */
  aside?: React.ReactNode;
  /** A picture left of the title (design navigation-v2 7d: the account art). */
  leading?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Design b2–b6: a sheet over the page, above the bottom bar. It is UI state, not a route. The
 * close button, the scrim and Escape close it. Focus stays inside while it is open and goes back
 * to the button that opened it.
 */
const MobileSheetComponent: React.FC<MobileSheetProps> = ({
  id,
  title,
  description,
  aside,
  leading,
  onClose,
  children,
}) => {
  const intl = useIntl();
  const sheetRef = useRef<HTMLDivElement>(null);
  useFocusTrap(sheetRef, true, onClose);
  const titleId = `${id}-title`;

  return (
    <>
      <div className={styles.scrim} onClick={onClose} aria-hidden="true" data-testid="mobile-sheet-scrim" />
      <div ref={sheetRef} id={id} className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={styles.grab} aria-hidden="true" />
        <div className={styles.head}>
          {leading}
          <div className={styles.headText}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {description !== undefined && <p className={styles.description}>{description}</p>}
          </div>
          {aside !== undefined && <span className={styles.aside}>{aside}</span>}
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={intl.formatMessage(messages.close)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        {children}
      </div>
    </>
  );
};

export const MobileSheet = TypedMemo(MobileSheetComponent);
